import { Link } from "react-router-dom";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import { useMemo } from "react";
import { CATEGORIES, getRelatedArticles } from "@/data/articles";
import SEOHead from "@/components/SEOHead";
import { normaliseSchemaDate } from "@/lib/schemaDate";

/**
 * Reusable layout for GEO articles. Every article component wraps its
 * body in <ArticleLayout meta={...}>{...}</ArticleLayout>.
 *
 * Responsibilities:
 *   - Hero (kicker + title + tldr + read time)
 *   - Prose styling (same tokens as LegalLayout — matte black + gold)
 *   - Breadcrumb back to /articles
 *   - Related articles footer strip
 *   - <script type="application/ld+json"> BlogPosting schema injected
 *     into <head> so AI crawlers + Google structured data get a clean
 *     citation-ready record for every article.
 *   - Sets document.title for SPA (crawlers that render JS see it)
 */
export default function ArticleLayout({ meta, children }) {
  const cat = CATEGORIES[meta.category];
  const related = useMemo(() => getRelatedArticles(meta.slug, 3), [meta.slug]);
  const canonicalUrl = `https://facetra.studio/articles/${meta.slug}`;

  // JSON-LD BlogPosting schema — cited by Google AI Overviews, Perplexity,
  // Bing Copilot. `mainEntityOfPage` binds the schema to the canonical URL,
  // `about` lists the primary topical entities so answer engines can
  // classify the article by subject.
  const ldjson = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": canonicalUrl,
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
    headline: meta.title,
    description: meta.tldr,
    image: "https://facetra.studio/og-image.jpg",
    keywords: meta.keywords || undefined,
    datePublished: normaliseSchemaDate(meta.published),
    dateModified: normaliseSchemaDate(meta.published),
    author: { "@type": "Organization", name: "Facetra", url: "https://facetra.studio/" },
    publisher: {
      "@type": "Organization",
      name: "Facetra",
      url: "https://facetra.studio/",
      logo: { "@type": "ImageObject", url: "https://facetra.studio/logo.png" },
    },
    inLanguage: "en-IN",
    articleSection: cat?.label || "Article",
    wordCount: meta.readMinutes ? meta.readMinutes * 200 : undefined,
    about: [
      { "@type": "Thing", name: "AI jewellery design" },
      { "@type": "Thing", name: "Indian jewellery" },
      { "@type": "Thing", name: "Jewellery CAD" },
    ],
  };

  // Set title + inject JSON-LD synchronously via react-helmet-async so
  // non-Googlebot crawlers (Bingbot, YandexBot, AI answer engines) also
  // see the tags at HTML parse time — see SEOHead.jsx for rationale.
  return (
    <div
      className="min-h-screen bg-[#0B0B0B] text-white relative overflow-hidden"
      data-testid={`article-${meta.slug}`}
    >
      <SEOHead
        title={meta.title}
        description={meta.tldr}
        canonical={canonicalUrl}
        type="article"
        keywords={meta.keywords}
        jsonLd={[
          ldjson,
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Facetra", item: "https://facetra.studio/" },
              { "@type": "ListItem", position: 2, name: "Articles", item: "https://facetra.studio/articles" },
              { "@type": "ListItem", position: 3, name: meta.title, item: canonicalUrl },
            ],
          },
        ]}
      />
      <div className="absolute inset-0 radial-gold-glow opacity-30 pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Top bar */}
      <header className="relative border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="article-home-link">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center shadow-[0_0_18px_rgba(212,175,55,0.35)]">
              <span className="font-display text-black text-sm font-bold">F</span>
            </div>
            <span className="font-display text-lg tracking-tight">Facetra</span>
          </Link>
          <Link
            to="/articles"
            data-testid="article-back-link"
            className="text-xs text-white/55 hover:text-[#D4AF37] inline-flex items-center gap-1.5 transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All articles
          </Link>
        </div>
      </header>

      <main className="relative max-w-3xl mx-auto px-6 lg:px-10 py-14">
        {/* Category kicker */}
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4">
          {cat?.label || "Article"}
        </div>

        {/* Title */}
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08]">
          {meta.title}
        </h1>

        {/* Meta row */}
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-white/45">
          <span>Published {formatDate(meta.published)}</span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {meta.readMinutes} min read
          </span>
        </div>

        {/* TL;DR — extracted first by every AI answer engine */}
        <div className="mt-8 rounded-xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/8 to-transparent p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">TL;DR</div>
          <p className="text-[14px] text-white/85 leading-relaxed">{meta.tldr}</p>
        </div>

        {/* Body */}
        <div className="mt-10 prose-legal">{children}</div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16 pt-8 border-t border-white/8">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
              Related reading
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/articles/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="group rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
                >
                  <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/40 mb-2">
                    {CATEGORIES[r.category]?.label}
                  </div>
                  <div className="text-[13px] text-white font-medium leading-snug group-hover:text-[#D4AF37] transition">
                    {r.title}
                  </div>
                  <div className="mt-3 text-[11px] text-white/40 inline-flex items-center gap-1">
                    Read <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-14 pt-8 border-t border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/40">
          <span>© {new Date().getFullYear()} Facetra Design Studio Private Limited</span>
          <div className="flex items-center gap-5">
            <Link to="/how-it-works" className="hover:text-white/70 transition">How it works</Link>
            <Link to="/glossary" className="hover:text-white/70 transition">Glossary</Link>
            <Link to="/articles" className="hover:text-white/70 transition">Articles</Link>
            <Link to="/contact" className="hover:text-white/70 transition">Contact</Link>
          </div>
        </div>
      </main>

      <style>{`
        .prose-legal h2 {
          font-family: 'Fraunces', serif;
          font-size: 1.6rem;
          letter-spacing: -0.01em;
          color: #fff;
          margin-top: 2.5rem;
          margin-bottom: 0.85rem;
        }
        .prose-legal h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #D4AF37;
          margin-top: 1.75rem;
          margin-bottom: 0.55rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .prose-legal p, .prose-legal li {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.72);
          line-height: 1.75;
        }
        .prose-legal p { margin-bottom: 0.95rem; }
        .prose-legal ul, .prose-legal ol { padding-left: 1.15rem; margin-bottom: 0.95rem; }
        .prose-legal ul li { list-style: disc; margin-bottom: 0.4rem; }
        .prose-legal ol li { list-style: decimal; margin-bottom: 0.4rem; }
        .prose-legal strong { color: #fff; font-weight: 600; }
        .prose-legal a { color: #D4AF37; text-decoration: underline; text-decoration-color: rgba(212,175,55,0.4); }
        .prose-legal a:hover { color: #F3CE5A; }
        .prose-legal blockquote {
          border-left: 2px solid rgba(212,175,55,0.4);
          padding-left: 1rem;
          margin: 1.25rem 0;
          color: rgba(255,255,255,0.75);
          font-style: italic;
        }
        .prose-legal table { width: 100%; border-collapse: collapse; margin: 1rem 0 1.25rem; }
        .prose-legal th, .prose-legal td {
          padding: 0.6rem 0.85rem;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          font-size: 0.88rem;
          text-align: left;
          vertical-align: top;
        }
        .prose-legal th { color: #D4AF37; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; font-size: 0.72rem; }
        .prose-legal code {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.85em;
          padding: 0.1rem 0.35rem;
          background: rgba(212,175,55,0.08);
          border: 1px solid rgba(212,175,55,0.15);
          border-radius: 4px;
          color: #F3CE5A;
        }
      `}</style>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
